/**
 * oEmbed utility functions for detecting and fetching embed data
 */

export type OEmbedProvider =
	| "youtube"
	| "spotify"
	| "apple-music"
	| "soundcloud"
	| "instagram"
	| "x"
	| null;

export interface OEmbedData {
	type: "video" | "rich" | "photo";
	html?: string;
	url?: string;
	thumbnail_url?: string;
	title?: string;
	author_name?: string;
	provider_name?: string;
	width?: number;
	height?: number;
}

/**
 * Detects which oEmbed provider a URL belongs to
 */
export function detectOEmbedProvider(url: string): OEmbedProvider {
	try {
		const urlObj = new URL(url);
		const hostname = urlObj.hostname.toLowerCase();

		// YouTube
		if (hostname.includes("youtube.com") || hostname.includes("youtu.be")) {
			return "youtube";
		}

		// Spotify
		if (hostname.includes("spotify.com")) {
			return "spotify";
		}

		// SoundCloud
		if (hostname.includes("soundcloud.com")) {
			return "soundcloud";
		}

		// Apple Music
		if (hostname.includes("music.apple.com")) {
			return "apple-music";
		}

		// Instagram
		if (hostname.includes("instagram.com")) {
			return "instagram";
		}

		// X (Twitter)
		if (hostname.includes("x.com") || hostname.includes("twitter.com")) {
			return "x";
		}

		return null;
	} catch {
		return null;
	}
}

/**
 * Extracts YouTube video ID from URL
 */
export function extractYouTubeId(url: string): string | null {
	try {
		const urlObj = new URL(url);
		const hostname = urlObj.hostname.toLowerCase();

		if (hostname.includes("youtube.com")) {
			const fromQuery = urlObj.searchParams.get("v");
			if (fromQuery) return fromQuery;
			const parts = urlObj.pathname.split("/").filter(Boolean);
			if (parts[0] === "shorts" && parts[1]) return parts[1];
			if (parts[0] === "embed" && parts[1]) return parts[1];
			return null;
		}

		if (hostname.includes("youtu.be")) {
			const id = urlObj.pathname.split("/").filter(Boolean)[0];
			return id ?? null;
		}

		return null;
	} catch {
		return null;
	}
}

/**
 * Extracts Spotify ID from URL
 */
export function extractSpotifyId(
	url: string,
): { type: string; id: string } | null {
	try {
		const urlObj = new URL(url);
		const pathParts = urlObj.pathname.split("/").filter(Boolean);

		if (pathParts.length >= 2) {
			const type = pathParts[0]; // track, album, playlist, etc.
			const id = pathParts[1];
			return { type, id };
		}

		return null;
	} catch {
		return null;
	}
}

/**
 * Extracts Apple Music ID from URL
 */
export function extractAppleMusicId(
	url: string,
): { type: string; id: string } | null {
	try {
		const urlObj = new URL(url);
		const pathParts = urlObj.pathname.split("/").filter(Boolean);

		// Apple Music URLs: /us/album/album-name/id123456789
		if (pathParts.length >= 3) {
			const knownType = pathParts.find((part) =>
				["album", "song", "playlist", "music-video"].includes(part),
			);
			const type = knownType ?? pathParts[pathParts.length - 2];
			const id = pathParts[pathParts.length - 1];
			return { type, id };
		}

		return null;
	} catch {
		return null;
	}
}

/**
 * Extracts Instagram post ID from URL
 */
export function extractInstagramId(url: string): string | null {
	try {
		const urlObj = new URL(url);
		const pathParts = urlObj.pathname.split("/").filter(Boolean);

		// Instagram URLs: /p/POST_ID/ or /reel/REEL_ID/
		if (pathParts.length >= 2) {
			return pathParts[1];
		}

		return null;
	} catch {
		return null;
	}
}

/**
 * Normalizes stored provider metadata or detects from URL.
 */
export function resolveEmbedProvider(
	url: string,
	providerFromMeta?: string | null,
): OEmbedProvider {
	if (providerFromMeta?.trim()) {
		const normalized = providerFromMeta.trim().toLowerCase().replace(/_/g, "-");
		if (normalized === "twitter") return "x";
		if (
			normalized === "youtube" ||
			normalized === "spotify" ||
			normalized === "apple-music" ||
			normalized === "soundcloud" ||
			normalized === "instagram" ||
			normalized === "x"
		) {
			return normalized;
		}
	}

	return detectOEmbedProvider(url);
}

/**
 * Extracts X/Twitter tweet ID from URL
 */
export function extractXId(url: string): string | null {
	try {
		const urlObj = new URL(url);
		const pathParts = urlObj.pathname.split("/").filter(Boolean);

		// X/Twitter URLs: /username/status/TWEET_ID
		if (pathParts.length >= 3 && pathParts[1] === "status") {
			return pathParts[2];
		}

		return null;
	} catch {
		return null;
	}
}

/**
 * Fetches oEmbed data from a provider
 * @deprecated Use `fetchOEmbedAction` from `@/lib/actions/oembed` instead for server-side fetching
 * This function is kept for backward compatibility but should not be used in new code
 */
export async function fetchOEmbedData(
	url: string,
	provider: OEmbedProvider,
): Promise<OEmbedData | null> {
	if (!provider) return null;

	try {
		let oembedUrl: string;

		switch (provider) {
			case "youtube": {
				const videoId = extractYouTubeId(url);
				if (!videoId) return null;
				oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`;
				break;
			}
			case "spotify": {
				oembedUrl = `https://embed.spotify.com/oembed?url=${encodeURIComponent(url)}`;
				break;
			}
			case "soundcloud": {
				oembedUrl = `https://soundcloud.com/oembed?format=json&url=${encodeURIComponent(url)}`;
				break;
			}
			case "apple-music": {
				oembedUrl = `https://embed.music.apple.com/us/oembed?url=${encodeURIComponent(url)}`;
				break;
			}
			case "instagram": {
				oembedUrl = `https://api.instagram.com/oembed?url=${encodeURIComponent(url)}`;
				break;
			}
			case "x": {
				oembedUrl = `https://publish.twitter.com/oembed?url=${encodeURIComponent(url)}`;
				break;
			}
			default:
				return null;
		}

		const response = await fetch(oembedUrl);
		if (!response.ok) return null;

		const data = await response.json();
		return data as OEmbedData;
	} catch (error) {
		console.error("Error fetching oEmbed data:", error);
		return null;
	}
}

/**
 * Checks if a URL is a valid oEmbed-supported link
 */
export function isValidOEmbedUrl(url: string): boolean {
	return detectOEmbedProvider(url) !== null;
}
